// MARK://dhk8/pos/hooks/useFloor
// react-query hooks over the floor client (dhk8-pos-floor). Query keys are
// scoped by the active tenant id so an env switch (Test ↔ Live) naturally
// invalidates the cached plano + refetches against the new tenant. Bug #81.

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRealtimeSubscription } from '@dockethub/realtime'
import type { TableDto } from '@dockethub/ui-restaurant'
import {
  getFloorState,
  saveFloorTables,
  setTableStatus,
  getPendingNotificationsByTable,
} from '../lib/clients/floor.js'
import { useTenantId } from './useTenantId.js'

export const floorKeys = {
  /** Tenant-scoped — keys MUST include tenantId or a switch leaves the cache
   *  serving the previous tenant's tables. */
  state: (tenantId: string | null) => ['floor', 'state', tenantId ?? 'no-tenant'] as const,
  /** Per-table PENDING guest-notification counts (E2 indicator). */
  notifications: (tenantId: string | null) =>
    ['floor', 'notifications', tenantId ?? 'no-tenant'] as const,
}

export function useFloorState() {
  const tenantId = useTenantId()
  const qc = useQueryClient()

  // El estado del salón debe verse EN EL MOMENTO. Antes sólo había polling
  // (staleTime 15s + interval 30s) y abrir una mesa desde el plano únicamente
  // hace `navigate('/order')` — ninguna mutación invalidaba esta query. Al
  // volver al plano dentro del staleTime se servía la foto anterior y la mesa
  // recién ocupada seguía pintada de verde; el interval tampoco corre con la
  // pestaña en segundo plano. Dos arreglos:
  //   1. refetchOnMount 'always' → volver al plano SIEMPRE refresca.
  //   2. suscripción realtime → pos-realtime ahora rutea `dhk8.pos.table.*` y
  //      `dhk8.pos.table_session.*` a `t:{tid}:orders`, el canal de staff que
  //      el POS ya escucha. Cada evento invalida y repinta al instante.
  const onFloorEvent = useCallback(() => {
    void qc.invalidateQueries({ queryKey: floorKeys.state(tenantId) })
  }, [qc, tenantId])
  useRealtimeSubscription(tenantId !== null ? `t:${tenantId}:orders` : '', onFloorEvent, [tenantId])

  return useQuery({
    queryKey: floorKeys.state(tenantId),
    queryFn: ({ signal }) => getFloorState(signal),
    enabled: !!tenantId,
    staleTime: 15_000,
    refetchOnMount: 'always',
    // Red de respaldo por si no hay gateway WS; el realtime es el camino rápido.
    refetchInterval: 30_000,
  })
}

/**
 * E2 — PENDING guest notifications grouped by `tableId`. Polls faster than the
 * floor layout (10s) because a comensal "llamar al mesero" should light up the
 * mesa quickly. Returns a `Map<tableId, GuestNotificationDto[]>`.
 */
export function useFloorNotifications() {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: floorKeys.notifications(tenantId),
    queryFn: () => getPendingNotificationsByTable(),
    enabled: !!tenantId,
    staleTime: 5_000,
    refetchInterval: 10_000,
  })
}

/** E1 — change a table's status from /floor (staff action). */
export function useSetTableStatus() {
  const qc = useQueryClient()
  const tenantId = useTenantId()
  return useMutation({
    mutationFn: ({ mapId, tableId, status }: { mapId: string; tableId: string; status: TableDto['status'] }) =>
      setTableStatus(mapId, tableId, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: floorKeys.state(tenantId) })
    },
  })
}

export function useSaveFloorTables() {
  const qc = useQueryClient()
  const tenantId = useTenantId()
  return useMutation({
    mutationFn: ({ mapId, tables }: { mapId: string; tables: TableDto[] }) => saveFloorTables(mapId, tables),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: floorKeys.state(tenantId) })
    },
  })
}
